import { useRef, useMemo, useEffect } from "react";
import { cloneDeep } from "lodash";
import { setVal } from "oolib";
import { mergeAnnoDataIntoAnnotationsTags, blockRegistry } from "@okf/ootils";

import { useQuickUpdateContent } from "@/pageTpls/ContributeTpl/queryHooks/useQuickUpdateContent";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { __CheckAccessToModule } from "@/utils/accessControl";

/**
 * Host-agnostic pipeline for editing annotations on a doc whose primary
 * editor is in readOnly mode (PublishedPage today, DataPivot next).
 *
 * Mirrors the form's useHandleContentChange dual-write semantics — Lexical
 * field value (main.<valuePath>) + consolidated annotations.tags — but
 * routes saves to /quickUpdateContent with skipCreateChunksProducer=true.
 *
 * Returns handleAnnotationChange(block, value, options) shaped to match the
 * form's onChange callback so block renderers can hand off to it without a
 * branching code path.
 */
export const useAnnotationEditPipeline = ({ contentType, contentId, content, onAfterSave, onOptimisticChange }) => {
  const { user } = useGetQueryData("userData") || {};
  const quickUpdate = useQuickUpdateContent();

  /**
   * Self-defense: callers (ViewBlockGenerator today) gate on the same check
   * before plumbing the pipeline through, but we re-check here so any future
   * host that consumes this hook directly can't accidentally let an unauthorised
   * user fire writes.
   */
  const canEditAnnotations = __CheckAccessToModule(user, "PUBLISH", "highlights");

  /**
   * Local snapshot of the doc — mutated in place across saves so that the
   * next merge sees the previous edit's annotations.tags state. Seeded from
   * the prop and re-seeded whenever the prop reference changes (e.g. after
   * a refetch).
   */
  const contentRef = useRef(content);
  if (contentRef.current !== content && content) {
    // Re-seed only if the host explicitly hands a new content object. We
    // never want to overwrite our locally-accumulated edits with a stale
    // prop, so equality check on the reference is enough.
    contentRef.current = content;
  }

  const mutate = quickUpdate.mutate;
  const userId = user?._id;
  const userName = user?.name;

  // Stash onAfterSave in a ref so the memoised handler below doesn't churn
  // on every host re-render (the host may pass a fresh closure each time).
  const onAfterSaveRef = useRef(onAfterSave);
  onAfterSaveRef.current = onAfterSave;
  // Optional synchronous hook fired with the freshly-computed content BEFORE
  // the POST, so a host can optimistically patch its own derived UI (e.g.
  // PublishedPage V3's sidebar cards). No-op when not provided (V2 doesn't).
  const onOptimisticChangeRef = useRef(onOptimisticChange);
  onOptimisticChangeRef.current = onOptimisticChange;

  // Serial save queue — ensures concurrent "Clear Tags" clicks don't fire
  // overlapping writes to the same document, which causes MongoDB write
  // conflicts (error code 112). Each save waits for the previous to settle.
  const saveQueueRef = useRef(Promise.resolve());
  // Tracks whether there is at least one save waiting in the queue. Used by
  // the beforeunload guard to decide whether to warn the user.
  const pendingSaveCountRef = useRef(0);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (pendingSaveCountRef.current > 0) {
        e.preventDefault();
        // Modern browsers show their own generic message; setting returnValue
        // is required to trigger the dialog in most browsers.
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleAnnotationChange = useMemo(
    () => (block, value /* , options */) => {
      if (!canEditAnnotations) return;
      const { valuePath } = block;
      const newC = cloneDeep(contentRef.current) || {};
      setVal(newC, valuePath, value);

      // Instance-aware: the block is passed so props.annotations.disable opts the field
      // out — a disabled field never writes annotation data, even if an onChange reaches here.
      const annotationsFieldMustBeUpdated =
        blockRegistry.hasCapability(block.comp, "annotation", block);

      if (annotationsFieldMustBeUpdated) {
        newC.annotations = {
          ...(newC.annotations || {}),
          tags: mergeAnnoDataIntoAnnotationsTags({
            prevObj: contentRef.current?.annotations?.tags,
            newLexValue: value,
            thisBlockValuePath: valuePath,
            author: userId ? { id: userId, name: userName } : undefined,
          }),
        };
      }

      contentRef.current = newC;

      // Optimistic: let the host refresh its derived UI immediately, before
      // the save round-trips.
      onOptimisticChangeRef.current?.(newC);

      const body = {
        $set: {
          [valuePath]: value,
          ...(annotationsFieldMustBeUpdated ? { annotations: newC.annotations } : {}),
        },
      };

      pendingSaveCountRef.current += 1;

      saveQueueRef.current = saveQueueRef.current.then(
        () =>
          new Promise((resolve) => {
            mutate(
              {
                contentType,
                contentId,
                body,
                skipCreateChunksProducer: true,
              },
              {
                onSuccess: () => {
                  // Notify peer clients on the same doc that something changed,
                  // so they can show a "doc updated, refresh" banner. The host
                  // wires this via the docCollaboration context.
                  onAfterSaveRef.current?.();
                  pendingSaveCountRef.current -= 1;
                  resolve();
                },
                onError: () => {
                  pendingSaveCountRef.current -= 1;
                  resolve();
                },
              }
            );
          })
      );
    },
    [contentType, contentId, userId, userName, mutate, canEditAnnotations]
  );

  return {
    handleAnnotationChange,
    isPending: quickUpdate.isLoading,
    canEdit: canEditAnnotations,
  };
};
